import { clsx } from 'clsx';
import kebabCase from 'lodash/kebabCase';
import React, { useMemo } from 'react';

import { TCurrency } from '@/config/locales';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';

import MapListing, { IProps as IListingProps } from '../MapListing/MapListing';

export interface IProps {
  /**
   * Classes to apply to the container element
   */
  className?: string;
  /**
   * Styles object to apply to the container element
   */
  style?: React.CSSProperties;
  /**
   * The currency to use when displaying prices
   */
  currency: TCurrency;
  /**
   * An array of listings to be displayed in the popup
   */
  listings: Omit<IListingProps, 'currency'>[];
  /**
   * A wheel event handler to possibly prevent scrolling the parent element
   */
  onWheelCapture?: (e: React.WheelEvent) => void;
  /**
   * A touch start event handler
   */
  onTouchStart?: (e: React.TouchEvent) => void;
  /**
   * A touch end event handler
   */
  onTouchEnd?: (e: React.TouchEvent) => void;
  /**
   * Whether the popup is on a wishlist page
   */
  isOnWishlist?: boolean;
  isEscapeMarker?: boolean;
}

const MapPopup = React.forwardRef<HTMLDivElement, IProps>(
  (
    {
      className = '',
      style,
      currency,
      listings,
      onWheelCapture,
      onTouchStart,
      onTouchEnd,
      isOnWishlist,
      isEscapeMarker,
    },
    ref,
  ) => {
    const isGroup = listings.length > 1;
    const testId = useMemo(
      () => (isGroup ? 'group-popup' : `${kebabCase(listings[0]?.title)}-popup`),
      [isGroup, listings],
    );
    const source = isGroup ? 'map-multi-marker' : 'map-single-marker';

    const listingTileUpdateEnabled = useExperimentIsEnabled(OptimizelyFlags.LISTING_TILE_UPDATE);

    if (listingTileUpdateEnabled && !isEscapeMarker) {
      // Grouped listings are going to be shown in a carousel
      // and we don't want to show the popup in that case.
      return isGroup ? null : (
        <div
          ref={ref}
          style={style}
          data-testid={testId}
          onWheelCapture={onWheelCapture}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}>
          {listings.map((listing, index) => (
            <MapListing
              key={listing.href}
              currency={currency}
              {...listing}
              source={source}
              isOnWishlist={isOnWishlist}
              index={index}
              isGroup={isGroup}
            />
          ))}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={clsx('p-0 overflow-y-auto bg-white shadow-100 rounded-box', className, {
          'max-h-60': !isOnWishlist || (isOnWishlist && isGroup),
          'w-[327px] md:w-[343px]': isOnWishlist && !isGroup,
        })}
        style={style}
        data-testid={testId}
        onWheelCapture={onWheelCapture}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}>
        {listings.map((listing, index) => (
          <div
            className={clsx('border-gray-200 last:border-b-0', {
              'p-2 border-b-0': !isOnWishlist || (isOnWishlist && isGroup),
            })}
            key={listing.href}>
            <MapListing
              currency={currency}
              {...listing}
              source={source}
              isOnWishlist={isOnWishlist}
              index={index}
              isGroup={isGroup}
            />
          </div>
        ))}
      </div>
    );
  },
);

MapPopup.displayName = 'MapPopup';

export default MapPopup;
